'use client';

import { Button, Dialog, DialogContent, useToggle } from '@commenda/kakashi';
import { TaskStatus } from '@prisma/client';
import { useNotification } from '../../../hooks/useNotification';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { updateTaskStatus } from '../../utils';
import { OnboardingWizardRenderer } from '../OnboardingWizardRenderer';

/**
 * Hardcoded legacy task for the US Tax & Accounting onboarding wizard
 * (`PROFILE_ONBOARDING_WIZARD`). Renders the standard service-request task card; its
 * action opens a full-screen dialog hosting the onboarding wizard. Finishing the wizard
 * marks the workflow step COMPLETED, advancing the service request.
 */
export const TaxAccountingOnboardingWizardTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;
  const { setNotification } = useNotification();
  const wizardModal = useToggle(false);

  const corporationId = serviceRequest?.corporationId;
  const companyId = serviceRequest?.companyId;

  const finishWizard = async () => {
    const ok = await updateTaskStatus({
      task,
      mutate,
      status: TaskStatus.COMPLETED,
      metadata: { source: 'PROFILE_ONBOARDING_WIZARD' },
    });
    if (!ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Could not complete onboarding. Please try again.',
      });
      return;
    }
    wizardModal.off();
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: ({ status }) => {
      if (status === TaskStatus.COMPLETED) {
        return {
          actions: null,
          label: (
            <ServiceRequestTaskLabel
              label={`${task.title} completed`}
              status={task.status}
              timestamp={task.updatedAt}
            />
          ),
        };
      }
      const actionLabel =
        status === TaskStatus.IN_PROGRESS ? 'Continue' : 'Start';
      return {
        actions:
          corporationId && companyId ? (
            <Button size="sm" onClick={wizardModal.show}>
              {actionLabel}
            </Button>
          ) : null,
        label: <ServiceRequestTaskLabel label={task.title} />,
      };
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      {corporationId && companyId && (
        <Dialog open={wizardModal.value} onOpenChange={wizardModal.off}>
          <DialogContent
            hideCloseIcon={task.status !== TaskStatus.COMPLETED}
            className="max-w-[100vw] h-[100vh] max-h-[100vh]"
          >
            <OnboardingWizardRenderer
              corporationId={corporationId}
              companyId={companyId}
              lockedFiscalYear={serviceRequest?.metadata?.fiscalYear ?? undefined}
              onFinish={finishWizard}
              onClose={wizardModal.off}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
